import React, { useEffect, useMemo, useState } from 'react';
import { Wallet as WalletIcon, ArrowDownToLine, ArrowUpFromLine, Plus, ArrowRight, ChevronDown, RefreshCw, Copy, Check, Send, Search, UserCheck, Coins, History as HistoryIcon, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import DividendTab from './DividendTab';


const StatusPill = ({ status }) => {
  const map = {
    PENDING: 'bg-amber-100 text-amber-800 border-amber-500/40',
    APPROVED: 'bg-emerald-100 text-emerald-800 border-emerald-500/40',
    REJECTED: 'bg-red-100 text-red-800 border-red-500/40',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${map[status] || 'bg-emerald-100 text-emerald-900'}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" /> {status}
    </span>
  );
};

const PortalCenter = ({ initialTab = 'wallet' }) => {
  const [tab, setTab] = useState(initialTab);

  return (
    <section className="px-4 md:px-10 py-6 pb-16">
      <div className="mx-auto max-w-[1240px]">
        <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-serif text-[30px] md:text-[34px] text-emerald-950 tracking-tight">Portal Center</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <TabBtn active={tab === 'wallet'} onClick={() => setTab('wallet')}>Wallet</TabBtn>
              <TabBtn active={tab === 'deposit'} onClick={() => setTab('deposit')}>Deposit</TabBtn>
              <TabBtn active={tab === 'withdraw'} onClick={() => setTab('withdraw')}>Withdraw</TabBtn>
              <TabBtn active={tab === 'transfer'} onClick={() => setTab('transfer')}>Transfer</TabBtn>
              <TabBtn active={tab === 'dividend'} onClick={() => setTab('dividend')}>Dividend</TabBtn>
              <TabBtn active={tab === 'history'} onClick={() => setTab('history')}>History</TabBtn>
            </div>

            <div className="mt-6">
              {tab === 'wallet' && <WalletTab />}
              {tab === 'deposit' && <DepositTab />}
              {tab === 'withdraw' && <WithdrawTab />}
              {tab === 'transfer' && <TransferTab />}
              {tab === 'dividend' && <DividendTab />}
              {tab === 'history' && <HistoryTab />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${active ? 'bg-emerald-950 text-emerald-50 shadow-sm' : 'bg-emerald-100/60 text-emerald-900 hover:bg-emerald-100'}`}
  >
    {children}
  </button>
);

/* -------- WALLET -------- */
const WalletTab = () => {
  const { user } = useAuth();
  const bonusTotal = Number(user?.bonus_wallet || 0);
  const locked = Number(user?.bonus_wallet_locked || 0);
  const bonusFree = Math.max(0, bonusTotal - locked);
  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <WalletCard label="Main Wallet" value={`$${(user?.main_wallet || 0).toFixed(2)}`} subvalue="Package purchases · Transfers" />
        <WalletCard label="Bonus Wallet" value={`$${bonusFree.toFixed(2)}`} highlight subvalue="Withdrawable earnings" />
        <WalletCard label="KYC / Locked Bonus" value={`$${locked.toFixed(2)}`} subvalue="Package purchase only" />
        <WalletCard label="Total Shares" value={String(user?.total_shares || 0)} subvalue="Approved Units" />
      </div>
      <PurchaseHistoryEmpty />
    </div>
  );
};

const WalletCard = ({ label, value, subvalue, highlight }) => (
  <div className={`rounded-2xl border p-6 text-center flex flex-col items-center justify-center gap-1 ${highlight ? 'bg-[#fff3cf] border-amber-500/25' : 'bg-[#fdf9e7] border-emerald-900/15'}`}>
    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-900/70">{label}</div>
    <div className={`font-serif text-4xl tracking-tight ${highlight ? 'text-amber-800' : 'text-emerald-900'}`}>{value}</div>
    {subvalue && <div className="text-[12px] text-emerald-900/60">{subvalue}</div>}
  </div>
);

const PurchaseHistoryEmpty = () => (
  <div className="mt-8 rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] px-4 py-8 text-center text-sm text-emerald-900/65">
    No dynamic purchases found on your account. Submit a request using the investment module.
  </div>
);

/* -------- DEPOSIT -------- */
const DepositTab = () => {
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('ERC20');
  const [tx, setTx] = useState('');
  const [settings, setSettings] = useState({ usdt_erc20_address: '', usdt_erc20_qr: '', usdt_trc20_address: '', usdt_trc20_qr: '' });
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null); // {verdict, reason, network, confirmations, actual_amount}

  const loadSettings = async () => {
    const { data } = await API.get('/settings');
    setSettings(data);
  };
  const loadHistory = async () => {
    const { data } = await API.get('/deposits/me');
    setHistory(data.items || []);
  };
  useEffect(() => { loadSettings(); loadHistory(); }, []);

  const address = network === 'ERC20' ? settings.usdt_erc20_address
    : network === 'BEP20' ? settings.usdt_bep20_address
    : settings.usdt_trc20_address;
  const qr = network === 'ERC20' ? settings.usdt_erc20_qr
    : network === 'BEP20' ? settings.usdt_bep20_qr
    : settings.usdt_trc20_qr;

  const chainLabel = network === 'ERC20' ? 'Ethereum' : network === 'BEP20' ? 'BNB Smart Chain' : 'Tron';

  const copyAddr = async () => {
    try { await navigator.clipboard.writeText(address); setCopied(true); toast({ title: 'Wallet address copied' }); setTimeout(() => setCopied(false), 1600); } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await API.post('/deposits', { amount: parseFloat(amount), network, tx_hash: tx });
      const cv = data?.chain_verify || {};
      setVerifyResult({ verdict: cv.verdict || 'PENDING', reason: cv.reason || '', confirmations: cv.confirmations, actual_amount: cv.actual_amount, network });
      if (cv.verdict === 'APPROVE') {
        toast({ title: 'Deposit approved instantly', description: `$${amount} USDT credited to your Main Wallet.` });
      } else if (cv.verdict === 'REJECT') {
        toast({ title: 'Deposit rejected', description: cv.reason || 'On-chain verification failed.' });
      } else {
        toast({ title: 'Awaiting confirmations', description: cv.reason || 'Admin will review shortly.' });
      }
      setAmount(''); setTx('');
      loadHistory();
    } catch (err) {
      setVerifyResult({ verdict: 'ERROR', reason: err.response?.data?.detail || 'Please try again.', network });
      toast({ title: 'Submission failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div>
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <ArrowDownToLine size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Top-up Wallet (USDT)</span>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LightField label="Amount (USD)">
              <input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required placeholder="e.g. 500.41" className="portal-input" />
            </LightField>
            <LightField label="Network">
              <div className="relative">
                <select value={network} onChange={(e) => setNetwork(e.target.value)} className="portal-input appearance-none pr-8">
                  <option value="ERC20">USDT ERC20 (Ethereum)</option>
                  <option value="BEP20">USDT BEP20 (BNB Smart Chain)</option>
                  <option value="TRC20">USDT TRC20 (Tron)</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-900/60 pointer-events-none" />
              </div>
            </LightField>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70 mb-2">Official Admin Payment Gateway</div>
            <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
              <div className="flex-1 min-w-0">
                <div className="rounded-xl bg-emerald-200/50 border border-emerald-800/25 px-4 py-3 text-emerald-900 font-mono text-[13px] break-all">
                  {address || '— address not set —'}
                </div>
                <button type="button" onClick={copyAddr} className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-emerald-800/30 bg-white/60 hover:bg-white text-emerald-900 text-[12px] px-3 py-1.5 transition">
                  {copied ? <Check size={12} /> : <Copy size={12} />} Copy Address
                </button>
                <p className="mt-2 text-[12px] text-emerald-900/60">Transfer USDT to this destination on {network} network, then paste the TX hash below.</p>
              </div>
              {qr ? (
                <img src={qr} alt="QR" className="h-28 w-28 rounded-lg border border-emerald-900/20 bg-white p-1 object-contain" />
              ) : (
                <div className="h-28 w-28 rounded-lg border border-dashed border-emerald-900/30 bg-white/50 flex items-center justify-center text-[10px] text-emerald-900/50 text-center px-2">
                  QR not uploaded
                </div>
              )}
            </div>
          </div>

          <LightField label="Transaction Hash / Reference">
            <input value={tx} onChange={(e) => setTx(e.target.value)} required placeholder="Enter block TX index reference" className="portal-input" disabled={verifying} />
          </LightField>

          {verifying && (
            <div data-testid="deposit-verifying-widget" className="flex items-center gap-3 rounded-xl bg-emerald-950/95 border border-emerald-800/40 px-4 py-3 text-emerald-50">
              <Loader2 size={18} className="animate-spin text-emerald-300" />
              <div className="flex-1">
                <div className="text-[13px] font-semibold">Auto-verifying on {chainLabel}…</div>
                <div className="text-[11px] text-emerald-200/70">Reading on-chain receipt · matching USDT transfer · confirming block finality</div>
              </div>
            </div>
          )}

          {verifyResult && !verifying && (
            <div
              data-testid={`deposit-verdict-${verifyResult.verdict.toLowerCase()}`}
              className={`flex items-start gap-3 rounded-xl px-4 py-3 border ${
                verifyResult.verdict === 'APPROVE' ? 'bg-emerald-100 border-emerald-700/40 text-emerald-950' :
                verifyResult.verdict === 'REJECT' ? 'bg-red-50 border-red-400/40 text-red-900' :
                'bg-amber-50 border-amber-400/40 text-amber-900'
              }`}
            >
              {verifyResult.verdict === 'APPROVE' ? <CheckCircle2 size={18} className="mt-0.5" /> :
                verifyResult.verdict === 'REJECT' ? <XCircle size={18} className="mt-0.5" /> :
                <Clock size={18} className="mt-0.5" />}
              <div className="flex-1">
                <div className="text-[13px] font-semibold">
                  {verifyResult.verdict === 'APPROVE' ? `Auto-approved on ${chainLabel}` :
                    verifyResult.verdict === 'REJECT' ? 'Verification failed' :
                    'Pending admin review'}
                </div>
                <div className="text-[12px] opacity-80 mt-0.5">{verifyResult.reason}</div>
                {verifyResult.verdict === 'APPROVE' && verifyResult.confirmations != null && (
                  <div className="text-[11px] opacity-70 mt-1">{verifyResult.confirmations} block confirmations · ${verifyResult.actual_amount} received</div>
                )}
              </div>
            </div>
          )}

          <button type="submit" disabled={verifying} data-testid="submit-deposit-btn" className="w-full rounded-xl bg-emerald-950 hover:bg-emerald-900 disabled:opacity-60 disabled:cursor-not-allowed text-emerald-50 font-medium py-3.5 transition flex items-center justify-center gap-2">
            {verifying ? (<><Loader2 size={16} className="animate-spin" /> Verifying on {chainLabel}…</>) : 'Submit Deposit Ticket'}
          </button>
        </form>
      </div>

      <HistoryList title="Deposit History" items={history} render={(d) => (
        <>
          <div>
            <div className="text-emerald-950 font-medium text-[14px]">${d.amount.toFixed(2)} USDT · <span className="text-emerald-800">{d.network}</span></div>
            <div className="text-[12px] text-emerald-900/60 font-mono truncate max-w-[380px]">TX: {d.tx_hash}</div>
            {d.status === 'REJECTED' && d.reason && (
              <div className="mt-1 text-[12px] text-red-700">Reason: {d.reason}</div>
            )}
          </div>
          <StatusPill status={d.status} />
        </>
      )} onSync={async () => {
        try {
          const { data } = await API.post('/deposits/reverify');
          if (data.approved > 0) {
            toast({ title: `${data.approved} deposit${data.approved > 1 ? 's' : ''} auto-approved`, description: 'On-chain confirmations found · wallet credited.' });
          } else if (data.still_pending > 0) {
            toast({ title: 'Still awaiting confirmations', description: `${data.still_pending} deposit${data.still_pending > 1 ? 's' : ''} not yet confirmed on-chain.` });
          } else {
            toast({ title: 'Log synced', description: 'No stuck deposits found.' });
          }
        } catch (err) {
          toast({ title: 'Sync failed', description: err.response?.data?.detail || 'Please try again.' });
        }
        loadHistory();
      }} />
    </div>
  );
};

/* -------- WITHDRAW -------- */
const WithdrawTab = () => {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('USDT ERC20 (Ethereum)');
  const [dest, setDest] = useState('');
  const [history, setHistory] = useState([]);

  const locked = Number(user?.bonus_wallet_locked || 0);
  const bonusTotal = Number(user?.bonus_wallet || 0);
  const withdrawable = Math.max(0, bonusTotal - locked);

  const loadHistory = async () => {
    const { data } = await API.get('/withdrawals/me');
    setHistory(data.items || []);
  };
  useEffect(() => { loadHistory(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt < 50) {
      return toast({ title: 'Minimum withdrawal is $50', description: 'Please enter an amount of $50 or more.' });
    }
    if (amt > withdrawable) {
      return toast({ title: 'Amount exceeds withdrawable balance', description: `You can withdraw up to $${withdrawable.toFixed(2)}.` });
    }
    try {
      await API.post('/withdrawals', { source: 'bonus', amount: amt, network, destination: dest });
      toast({ title: 'Withdrawal submitted', description: `$${amt.toFixed(2)} USDT pending admin review. Balance has been reserved.` });
      setAmount(''); setDest('');
      loadHistory();
      refresh();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Please try again.' });
    }
  };

  return (
    <div>
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <ArrowUpFromLine size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Withdrawal Request (USDT)</span>
        </div>

        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-100/50 px-3 py-2 text-[12px] text-amber-900">
          <b>Policy:</b> Only Bonus Wallet earnings are withdrawable. Main Wallet balance and KYC/Received-Transfer credits are reserved for package purchases and cannot be withdrawn.
        </div>

        <form onSubmit={submit} className="mt-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LightField label="Source Wallet">
              <div className="relative">
                <select value="bonus" disabled className="portal-input appearance-none pr-8 opacity-90 cursor-not-allowed">
                  <option value="bonus">Bonus Wallet (${withdrawable.toFixed(2)} withdrawable · ${locked.toFixed(2)} locked)</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-900/60 pointer-events-none" />
              </div>
            </LightField>
            <LightField label={`Amount (USD) · Min $50 · Max $${withdrawable.toFixed(2)}`}>
              <input
                data-testid="withdraw-amount"
                type="number"
                min="50"
                step="1"
                max={withdrawable || undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                placeholder="Minimum $50"
                className="portal-input"
              />
            </LightField>
            <LightField label="Withdraw Network">
              <div className="relative">
                <select value={network} onChange={(e) => setNetwork(e.target.value)} className="portal-input appearance-none pr-8">
                  <option>USDT ERC20 (Ethereum)</option>
                  <option>USDT TRC20 (Tron)</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-900/60 pointer-events-none" />
              </div>
            </LightField>
            <LightField label="Destination Address">
              <input value={dest} onChange={(e) => setDest(e.target.value)} required placeholder="Enter receiving wallet" className="portal-input" />
            </LightField>
          </div>

          <button
            data-testid="withdraw-submit"
            type="submit"
            disabled={withdrawable < 50}
            className="w-full rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 transition"
          >
            {withdrawable < 50 ? `Minimum $50 needed (Available: $${withdrawable.toFixed(2)})` : 'Confirm Withdrawal'}
          </button>
        </form>
      </div>

      <HistoryList title="Withdrawal History" items={history} render={(w) => (
        <>
          <div>
            <div className="text-emerald-950 font-medium text-[14px]">${w.amount.toFixed(2)} · <span className="text-emerald-800">{w.network}</span></div>
            <div className="text-[12px] text-emerald-900/60 font-mono truncate max-w-[380px]">→ {w.destination}</div>
            {w.status === 'REJECTED' && w.reason && (
              <div className="mt-1 text-[12px] text-red-700">Reason: {w.reason}</div>
            )}
          </div>
          <StatusPill status={w.status} />
        </>
      )} onSync={loadHistory} />
    </div>
  );
};

/* -------- TRANSFER -------- */
const TransferTab = () => {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [recipient, setRecipient] = useState(null);
  const [searching, setSearching] = useState(false);
  const [source, setSource] = useState('main');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState({ sent: [], received: [] });

  const main = Number(user?.main_wallet || 0);
  const bonusTotal = Number(user?.bonus_wallet || 0);
  const locked = Number(user?.bonus_wallet_locked || 0);
  const bonusUnlocked = Math.max(0, bonusTotal - locked);

  const loadHistory = async () => {
    try {
      const { data } = await API.get('/transfers/me');
      setHistory({ sent: data.sent || [], received: data.received || [] });
    } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const searchRecipient = async () => {
    if (!email.trim()) return;
    setSearching(true);
    setRecipient(null);
    try {
      const { data } = await API.get('/users/search', { params: { email: email.trim() } });
      setRecipient(data);
      toast({ title: 'Recipient verified', description: `${data.name} · ${data.email}` });
    } catch (err) {
      toast({ title: 'Not found', description: err.response?.data?.detail || 'No user found' });
    } finally {
      setSearching(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!recipient) return toast({ title: 'Search recipient first' });
    setSubmitting(true);
    try {
      await API.post('/transfers', {
        recipient_id: recipient.id,
        source,
        amount: parseFloat(amount),
        note: note || undefined,
      });
      toast({ title: 'Transfer completed', description: `$${amount} sent to ${recipient.name}. Recipient can use this only for package purchases.` });
      setEmail(''); setRecipient(null); setAmount(''); setNote('');
      loadHistory();
      refresh();
    } catch (err) {
      toast({ title: 'Transfer failed', description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const balanceForSource = source === 'main' ? main : source === 'bonus' ? bonusUnlocked : locked;

  return (
    <div>
      <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
        <div className="flex items-center gap-2">
          <Send size={16} className="text-emerald-700" />
          <span className="text-emerald-900 font-semibold text-[15px]">Send Funds to Another Investor</span>
        </div>
        <div className="mt-2 text-[12px] text-emerald-900/70">
          Transfer from your Main, Bonus, or KYC Bonus wallet to another SkywayGreen investor. Received funds go into the recipient's <b>package-purchase-only</b> balance (non-withdrawable).
        </div>

        <form onSubmit={submit} className="mt-5 space-y-5">
          {/* Step 1: Recipient */}
          <LightField label="Recipient Email">
            <div className="flex gap-2 flex-wrap">
              <input
                data-testid="transfer-email"
                type="email"
                required
                value={email}
                onChange={(e) => { setEmail(e.target.value); setRecipient(null); }}
                placeholder="recipient@example.com"
                className="portal-input flex-1 min-w-[200px]"
              />
              <button
                data-testid="transfer-search-btn"
                type="button"
                onClick={searchRecipient}
                disabled={!email.trim() || searching}
                className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-900 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold text-sm px-4 py-2 transition"
              >
                {searching ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                {searching ? 'Searching...' : 'Search & Verify'}
              </button>
            </div>
          </LightField>

          {recipient && (
            <div data-testid="transfer-recipient-card" className="rounded-xl border border-emerald-600/30 bg-emerald-50 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center text-white">
                <UserCheck size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-emerald-950 font-semibold text-[14px]">{recipient.name}</div>
                <div className="text-[12px] text-emerald-800/70">{recipient.email} · KYC {recipient.kyc_status}</div>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-200/60 px-2 py-1 rounded">Verified</span>
            </div>
          )}

          {/* Step 2: Source + amount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <LightField label="From Wallet">
              <div className="relative">
                <select data-testid="transfer-source" value={source} onChange={(e) => { setSource(e.target.value); setAmount(''); }} className="portal-input appearance-none pr-8">
                  <option value="main">Main Wallet (${main.toFixed(2)})</option>
                  <option value="bonus">Bonus Wallet — Withdrawable (${bonusUnlocked.toFixed(2)})</option>
                  <option value="kyc_bonus">KYC/Locked Bonus (${locked.toFixed(2)})</option>
                </select>
                <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-900/60 pointer-events-none" />
              </div>
            </LightField>
            <LightField label={`Amount (USD) · Max $${balanceForSource.toFixed(2)}`}>
              <input
                data-testid="transfer-amount"
                type="number"
                min="1"
                step="0.01"
                max={balanceForSource || undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                placeholder="e.g. 50"
                className="portal-input"
              />
            </LightField>
          </div>

          <LightField label="Note (optional)">
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={280}
              placeholder="e.g. Package top-up support"
              className="portal-input"
            />
          </LightField>

          <button
            data-testid="transfer-submit"
            type="submit"
            disabled={submitting || !recipient || balanceForSource <= 0}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 transition"
          >
            <Send size={16} />
            {submitting ? 'Sending...' : `Send ${amount ? '$' + amount : 'Funds'} to Recipient`}
          </button>
        </form>
      </div>

      {/* History */}
      {(history.sent.length > 0 || history.received.length > 0) && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TransferHistoryCard title="Sent Transfers" items={history.sent} direction="out" />
          <TransferHistoryCard title="Received Transfers" items={history.received} direction="in" />
        </div>
      )}
    </div>
  );
};

const TransferHistoryCard = ({ title, items, direction }) => {
  const arrow = direction === 'out' ? '→' : '←';
  const color = direction === 'out' ? 'text-orange-800' : 'text-emerald-800';
  return (
    <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-4">
      <div className="text-emerald-900 font-semibold text-[14px] mb-3">{title} ({items.length})</div>
      {items.length === 0 ? (
        <div className="text-[12px] text-emerald-900/50 italic">No transfers yet.</div>
      ) : (
        <ul className="space-y-2">
          {items.slice(0, 10).map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-3 rounded-lg border border-emerald-900/10 bg-white px-3 py-2">
              <div className="min-w-0">
                <div className={`text-[13px] font-medium ${color}`}>
                  {arrow} ${Number(t.amount).toFixed(2)} · {direction === 'out' ? t.recipient_email : t.sender_email}
                </div>
                <div className="text-[11px] text-emerald-900/50">
                  {t.source === 'main' ? 'Main' : t.source === 'bonus' ? 'Bonus' : 'KYC Bonus'} · {new Date(t.created_at).toLocaleDateString()}
                  {t.note ? ` · "${t.note}"` : ''}
                </div>
              </div>
              <StatusPill status={t.status} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const LightField = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/80 mb-2">{label}</span>
    {children}
  </label>
);

const HistoryList = ({ title, items, render, onSync }) => (
  <div className="mt-8">
    <div className="flex items-center justify-between gap-3">
      <h3 className="font-serif text-emerald-950 text-xl">{title}</h3>
      <button
        onClick={onSync}
        className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-900/20 bg-white/60 hover:bg-white text-emerald-900 text-sm font-medium px-3 py-2 transition"
      >
        <RefreshCw size={14} /> Sync Log
      </button>
    </div>
    <div className="mt-4">
      {items.length === 0 ? (
        <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] px-4 py-8 text-center text-sm text-emerald-900/65">
          No entries yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li key={it.id} className="rounded-xl border border-emerald-900/15 bg-[#fdf9e7] px-4 py-3 flex items-center justify-between gap-3">
              {render(it)}
            </li>
          ))}
        </ul>
      )}
    </div>
  </div>
);

export default PortalCenter;

/* -------- WALLET HISTORY -------- */
const TYPE_LABEL = {
  DEPOSIT: 'Deposit',
  WITHDRAWAL: 'Withdrawal',
  WITHDRAWAL_REFUND: 'Withdrawal Refund',
  PURCHASE: 'Package Purchase',
  COMMISSION: 'Referral Commission',
  TRANSFER_SENT: 'Transfer Sent',
  TRANSFER_RECEIVED: 'Transfer Received',
  DIVIDEND: 'Dividend Payout',
  KYC_BONUS: 'KYC Welcome Bonus',
};

const WALLET_LABEL = {
  main: 'Main',
  bonus: 'Bonus',
  kyc_locked: 'KYC / Locked',
  external: 'External',
};

const HistoryTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState('all');
  const [direction, setDirection] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/wallet/history');
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((i) => {
    if (wallet !== 'all' && i.wallet !== wallet) return false;
    if (direction !== 'all' && i.direction !== direction) return false;
    return true;
  }), [items, wallet, direction]);

  const totalCredit = filtered.filter((i) => i.direction === 'credit').reduce((s, i) => s + Number(i.amount || 0), 0);
  const totalDebit = filtered.filter((i) => i.direction === 'debit').reduce((s, i) => s + Number(i.amount || 0), 0);

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard label="Total Entries" value={String(filtered.length)} icon={HistoryIcon} />
        <SummaryCard label="Total Credits" value={`$${totalCredit.toFixed(2)}`} tone="credit" />
        <SummaryCard label="Total Debits" value={`$${totalDebit.toFixed(2)}`} tone="debit" />
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/60">Wallet</span>
          {[
            ['all', 'All'],
            ['main', 'Main'],
            ['bonus', 'Bonus'],
            ['kyc_locked', 'KYC / Locked'],
            ['external', 'External'],
          ].map(([v, l]) => (
            <FilterChip key={v} active={wallet === v} onClick={() => setWallet(v)}>{l}</FilterChip>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/60">Direction</span>
          {[
            ['all', 'All'],
            ['credit', 'Credits'],
            ['debit', 'Debits'],
          ].map(([v, l]) => (
            <FilterChip key={v} active={direction === v} onClick={() => setDirection(v)}>{l}</FilterChip>
          ))}
          <button
            onClick={load}
            className="inline-flex items-center gap-1 rounded-xl border border-emerald-900/20 bg-white/60 px-3 py-1.5 text-[12px] text-emerald-900 hover:bg-emerald-100 transition"
            data-testid="wallet-history-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-emerald-900/15 bg-[#fdf9e7]">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-emerald-900/60">Loading transactions…</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-emerald-900/60">No wallet transactions match the current filters.</div>
        ) : (
          <ul className="divide-y divide-emerald-900/10">
            {filtered.map((i) => (
              <li
                key={i.id}
                className="px-4 md:px-5 py-3.5 flex items-start justify-between gap-3 hover:bg-emerald-100/40 transition"
                data-testid={`wallet-history-row-${i.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-emerald-950 font-medium text-sm">{TYPE_LABEL[i.type] || i.type}</span>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
                      i.wallet === 'main' ? 'bg-emerald-100 text-emerald-800 border-emerald-500/40'
                      : i.wallet === 'bonus' ? 'bg-amber-100 text-amber-800 border-amber-500/40'
                      : i.wallet === 'kyc_locked' ? 'bg-yellow-100 text-yellow-800 border-yellow-500/40'
                      : 'bg-sky-100 text-sky-800 border-sky-500/40'
                    }`}>{WALLET_LABEL[i.wallet] || i.wallet}</span>
                    <StatusPill status={i.status} />
                  </div>
                  <div className="mt-1 text-[12px] text-emerald-900/60 truncate">{i.description}</div>
                  <div className="mt-0.5 text-[11px] text-emerald-900/40">{fmtDate(i.at)}</div>
                </div>
                <div className={`text-lg font-serif font-semibold whitespace-nowrap ${i.direction === 'credit' ? 'text-emerald-700' : 'text-red-700'}`}>
                  {i.direction === 'credit' ? '+' : '−'}${Number(i.amount || 0).toFixed(2)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const FilterChip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-full px-3 py-1 text-[12px] font-medium transition ${active ? 'bg-emerald-950 text-emerald-50' : 'bg-white/70 text-emerald-900 border border-emerald-900/20 hover:bg-emerald-100'}`}
  >
    {children}
  </button>
);

const SummaryCard = ({ label, value, tone, icon: Icon }) => (
  <div className={`rounded-2xl border p-5 flex items-center gap-4 ${tone === 'credit' ? 'bg-emerald-50/70 border-emerald-500/25' : tone === 'debit' ? 'bg-red-50/70 border-red-500/25' : 'bg-[#fdf9e7] border-emerald-900/15'}`}>
    {Icon && <div className="h-10 w-10 rounded-xl bg-emerald-950 text-emerald-200 flex items-center justify-center"><Icon size={18} /></div>}
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/60">{label}</div>
      <div className={`font-serif text-2xl mt-0.5 ${tone === 'credit' ? 'text-emerald-800' : tone === 'debit' ? 'text-red-800' : 'text-emerald-950'}`}>{value}</div>
    </div>
  </div>
);

const fmtDate = (v) => {
  if (!v) return '';
  try {
    const d = new Date(v);
    return d.toLocaleString(undefined, { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return String(v);
  }
};
